import { useState, type CSSProperties, type ComponentType, type ReactElement } from "react";
import {
  ArrowRight, Calendar, Home, MapPin, Thermometer, Wind, Camera, AlertTriangle,
  Building2, ClipboardList, FlaskConical, Image as ImageIcon, ListChecks,
  PenLine, Compass, Layers, ChevronLeft, ChevronRight, HelpCircle,
} from "lucide-react";
import overviewImage from "@/assets/damp-survey/sample-report/overview.png.asset.json";
import propertyContextImage from "@/assets/damp-survey/sample-report/property-context.png.asset.json";
import externalChecksImage from "@/assets/damp-survey/sample-report/external-checks.png.asset.json";
import externalGutterImage from "@/assets/damp-survey/sample-report/external-gutter.png.asset.json";
import windowCondensationImage from "@/assets/damp-survey/sample-report/window-condensation.png.asset.json";
import kitchenVentilationImage from "@/assets/damp-survey/sample-report/kitchen-ventilation.png.asset.json";
import bathroomVentilationImage from "@/assets/damp-survey/sample-report/bathroom-ventilation.png.asset.json";
import internalWideImage from "@/assets/damp-survey/sample-report/internal-wide.png.asset.json";
import furnitureWallImage from "@/assets/damp-survey/sample-report/furniture-wall.png.asset.json";
import moistureMeterImage from "@/assets/damp-survey/sample-report/moisture-meter.png.asset.json";
import evidenceGalleryImage from "@/assets/damp-survey/sample-report/evidence-gallery.png.asset.json";
import thermalComparisonImage from "@/assets/damp-survey/sample-report/thermal-comparison.png.asset.json";

const CTA_LABEL = "Book A Damp Assessment";

function scrollToHeroForm() {
  if (typeof window === "undefined") return;
  document.getElementById("damp-survey-hero-form")?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

/* ──────────────── Primitives ──────────────── */

function NamedImageSlot({
  label,
  caption,
  pin,
  src,
  alt,
}: {
  label: string;
  caption: string;
  pin?: number;
  src?: string;
  alt?: string;
}) {
  return (
    <figure className="relative w-full rounded-xl overflow-hidden border border-border bg-gradient-to-br from-secondary to-muted">
      <div className="relative aspect-[4/3] flex items-center justify-center">
        {src ? (
          <img
            src={src}
            alt={alt ?? label}
            loading="lazy"
            className="h-full w-full max-w-full object-cover"
          />
        ) : (
          <div className="relative z-10 flex flex-col items-center text-center px-4">
            <ImageIcon className="h-7 w-7 text-primary/50 mb-1.5" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {label}
            </span>
            <span className="text-[10px] text-muted-foreground/80 mt-0.5">Image Slot</span>
          </div>
        )}
        {pin !== undefined && (
          <span className="absolute top-2 left-2 h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shadow z-20">
            {pin}
          </span>
        )}
      </div>
      <figcaption className="p-2.5 text-[11px] leading-snug text-muted-foreground bg-card/80 border-t border-border">
        {caption}
      </figcaption>
    </figure>
  );
}

type Status = "Normal" | "Elevated" | "At Risk";
function ReadingRow({ room, wme, status }: { room: string; wme: string; status: Status }) {
  const tone =
    status === "Normal"
      ? "bg-primary/10 text-primary"
      : status === "Elevated"
      ? "bg-accent-soft text-accent"
      : "bg-destructive/10 text-destructive";
  return (
    <tr className="border-t border-border">
      <td className="py-2.5 px-3 text-sm">{room}</td>
      <td className="py-2.5 px-3 text-sm font-mono">{wme}</td>
      <td className="py-2.5 px-3">
        <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${tone}`}>
          {status}
        </span>
      </td>
    </tr>
  );
}

type ExplanationProps = {
  helps: string;
  essential: string;
  full: string;
  why: string;
};

function ExplanationPanel(p: ExplanationProps) {
  return (
    <aside className="lg:sticky lg:top-24 self-start">
      <div className="bg-secondary/60 border border-border rounded-2xl p-3 md:p-4 text-sm leading-relaxed space-y-3">
        <p className="text-foreground flex items-start gap-1.5 leading-snug">
          <HelpCircle className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
          <span>{p.helps}</span>
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="rounded-lg border border-border bg-card p-2.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
              Essential £99
            </p>
            <p className="text-[12px] font-medium text-foreground leading-snug">{p.essential}</p>
          </div>
          <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-2.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-primary mb-1">
              Full £249
            </p>
            <p className="text-[12px] font-medium text-foreground leading-snug">{p.full}</p>
          </div>
        </div>
        <p className="text-[12px] italic text-muted-foreground leading-snug">{p.why}</p>
        <button
          type="button"
          onClick={scrollToHeroForm}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-accent text-accent-foreground text-xs font-bold hover:bg-accent/90 transition-colors shadow-cta"
        >
          <Calendar className="h-4 w-4" /> {CTA_LABEL}
        </button>
      </div>
    </aside>
  );
}

/* ──────────────── Section bodies ──────────────── */

function SectionOverview() {
  return (
    <div className="space-y-4">
      <NamedImageSlot
        label="Property overview"
        caption="Front elevation photo used to identify the property and frame the assessment context."
        src={overviewImage.url}
        alt="Example front elevation of a residential property used in the sample damp report overview"
      />
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div className="bg-secondary/60 rounded-lg p-3"><span className="text-xs text-muted-foreground">Reference</span><div className="font-mono font-semibold">SAMPLE-CRS-001</div></div>
        <div className="bg-secondary/60 rounded-lg p-3"><span className="text-xs text-muted-foreground">Property type</span><div className="font-semibold">Example terraced home</div></div>
        <div className="bg-secondary/60 rounded-lg p-3"><span className="text-xs text-muted-foreground">Inspection type</span><div className="font-semibold">Full Diagnostic Damp Survey</div></div>
        <div className="bg-secondary/60 rounded-lg p-3"><span className="text-xs text-muted-foreground">Risk tier</span><div className="font-semibold text-accent">Moderate</div></div>
        <div className="bg-secondary/60 rounded-lg p-3"><span className="text-xs text-muted-foreground">Example score</span><div className="font-semibold">54 / 100</div></div>
        <div className="bg-secondary/60 rounded-lg p-3"><span className="text-xs text-muted-foreground">Survey selected</span><div className="font-semibold">Full Diagnostic</div></div>
      </div>
      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-2">Key concern areas</p>
        <div className="flex flex-wrap gap-2">
          {["Moisture And Ventilation", "External Fabric", "Internal Staining"].map((c) => (
            <span key={c} className="text-xs font-medium bg-primary/10 text-primary px-2.5 py-1 rounded-full">{c}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionScope() {
  return (
    <div className="space-y-4 text-sm">
      <NamedImageSlot
        label="Property exterior"
        caption="Example property context, construction and exposure considered."
        src={propertyContextImage.url}
        alt="Side elevation of the sample property showing access path and external wall condition"
      />
      <dl className="space-y-2">
        <div><dt className="font-semibold inline">Reason for instruction: </dt><dd className="inline text-muted-foreground">Recurring mould to a bedroom external corner and concerns about kitchen ventilation.</dd></div>
        <div><dt className="font-semibold inline">Areas inspected: </dt><dd className="inline text-muted-foreground">Front bedroom, kitchen, hallway, external front and rear elevations.</dd></div>
        <div><dt className="font-semibold inline">Exclusions: </dt><dd className="inline text-muted-foreground">Loft, sub floor void and rooms not made accessible at the time of inspection.</dd></div>
      </dl>
      <p className="text-xs text-muted-foreground italic">This report only covers what was inspected.</p>
    </div>
  );
}

function SectionProperty() {
  return (
    <div className="space-y-4">
      <NamedImageSlot
        label="Property exterior context"
        caption="Example property context, construction and exposure considered."
        src={propertyContextImage.url}
        alt="Property context photo showing external wall, roofline and site exposure"
      />
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        {[
          ["Property age", "Circa 1905"],
          ["Wall type", "Solid masonry, 9 inch"],
          ["Exposure", "Moderately exposed, urban"],
          ["External finish", "Painted render to front"],
          ["Roof and rainwater", "Slate pitched, cast iron rainwater goods"],
          ["Ventilation context", "Chimney capped, no PIV, limited trickle vents"],
        ].map(([k, v]) => (
          <div key={k} className="bg-secondary/60 rounded-lg p-3">
            <span className="text-xs text-muted-foreground">{k}</span>
            <div className="font-medium">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionExternal() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <NamedImageSlot
          label="External render"
          caption="External render crack and low level wall condition checked as a possible moisture pathway."
          src={externalChecksImage.url}
          alt="Close up of rendered external wall with cracking used as evidence in the external checks section"
        />
        <NamedImageSlot
          label="Rainwater goods"
          caption="Rainwater goods and roof junctions checked for water tracking."
          src={externalGutterImage.url}
          alt="Gutter and downpipe detail showing an external roofline issue"
        />
      </div>
      <ul className="text-sm space-y-1.5">
        {["Blocked or overflowing gutter (front elevation)",
          "Failed sealant or flashing (chimney abutment)",
          "Cracked render or pointing (low level, front)",
          "Water tracking path observed below cill"].map((t) => (
          <li key={t} className="flex gap-2"><MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />{t}</li>
        ))}
      </ul>
    </div>
  );
}

function SectionVentilation() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <NamedImageSlot label="Window condensation" caption="Condensation and background ventilation considered." src={windowCondensationImage.url} alt="Condensation visible on a window with trickle vents in the sample report" />
        <NamedImageSlot label="Kitchen ventilation" caption="Cooking moisture and extraction checked." src={kitchenVentilationImage.url} alt="Kitchen showing signs of moisture build up and ventilation concerns" />
        <NamedImageSlot label="Bathroom ventilation" caption="Wet room ventilation and steam clearance checked." src={bathroomVentilationImage.url} alt="Bathroom with extractor vent used as ventilation evidence in the sample report" />
        <NamedImageSlot label="Trickle vent detail" caption="Background ventilation provision reviewed." src={windowCondensationImage.url} alt="Close view of window area with condensation and trickle vents" />
      </div>
      <p className="text-xs text-muted-foreground italic">Your ventilation and moisture load are reviewed as part of the whole home picture, not used to attribute blame.</p>
    </div>
  );
}

function SectionRoom() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <NamedImageSlot label="Bedroom external wall, wide shot" caption="Visible staining pattern and affected wall location recorded." src={internalWideImage.url} alt="Wide shot of bedroom wall with mould and staining at low level" />
        <NamedImageSlot label="Furniture against external wall" caption="Restricted airflow behind furniture noted as a contributing factor." src={furnitureWallImage.url} alt="Bedroom furniture positioned against an external wall with mould visible behind it" />
      </div>
      <div className="border border-border rounded-xl p-4 bg-secondary/40 text-sm space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <span className="font-bold text-foreground">Bedroom, external corner</span>
          <span className="text-[11px] font-semibold bg-accent-soft text-accent px-2 py-0.5 rounded-full">Moderate</span>
        </div>
        <div><span className="font-semibold">Symptom: </span><span className="text-muted-foreground">Mould growth and surface staining to cold external corner.</span></div>
        <div><span className="font-semibold">Location: </span><span className="text-muted-foreground">North facing external wall, behind wardrobe.</span></div>
        <div><span className="font-semibold">Likely contributing factors: </span><span className="text-muted-foreground">Surface condensation on a poorly insulated reveal combined with limited air movement.</span></div>
        <div><span className="font-semibold">Linked evidence: </span><span className="text-muted-foreground">External rainwater goods (Section: External), bedroom WME readings (Section: Readings).</span></div>
      </div>
    </div>
  );
}

function SectionReadings() {
  return (
    <div className="space-y-4">
      <NamedImageSlot label="Moisture meter on wall" caption="Moisture meter reading taken on an affected wall to support the diagnosis." src={moistureMeterImage.url} alt="Moisture meter held against a damp affected wall showing a high reading" />
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="text-xs uppercase text-muted-foreground">
              <th className="py-2 px-3">Location</th>
              <th className="py-2 px-3">WME</th>
              <th className="py-2 px-3">Status</th>
            </tr>
          </thead>
          <tbody>
            <ReadingRow room="Bedroom, external corner" wme="18%" status="Elevated" />
            <ReadingRow room="Kitchen, below window" wme="12%" status="Normal" />
            <ReadingRow room="Hallway, skirting" wme="22%" status="At Risk" />
            <ReadingRow room="Living room, party wall" wme="10%" status="Normal" />
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground italic">Readings support diagnosis and are interpreted in context.</p>
    </div>
  );
}

function SectionThermal() {
  return (
    <div className="space-y-4">
      <NamedImageSlot
        label="Thermal vs normal comparison"
        caption="Side by side comparison of a normal photo and the thermal image. Cooler corners and reveals show where condensation and mould risk is higher. Thermal imaging is included in the Full Diagnostic Survey only."
        src={thermalComparisonImage.url}
        alt="Side by side comparison of a window corner shown in normal photography and thermal imaging, with cooler areas highlighting thermal bridging"
      />
      <p className="text-xs text-muted-foreground">Cooler surfaces may indicate thermal bridging or reduced insulation performance.</p>
    </div>
  );
}

function SectionPhotos() {
  return (
    <div className="space-y-3">
      <NamedImageSlot
        label="Annotated evidence gallery"
        caption="Numbered evidence pack linking findings to what was seen at the property: (1) cracked render at window cill, (2) blocked rainwater goods, (3) internal staining behind a radiator, (4) window condensation and mould to the seal."
        src={evidenceGalleryImage.url}
        alt="Annotated 2x2 evidence gallery showing external render crack, blocked gutter and downpipe, internal staining and window condensation"
      />
      <p className="text-[11px] text-muted-foreground italic">Photographic evidence and annotations, Full Diagnostic Survey.</p>
    </div>
  );
}

function SectionRecommendations() {
  return (
    <div className="space-y-4">
      <NamedImageSlot label="Recommendation area" caption="Findings translated into practical next steps and repair scope." />
      <div className="border border-border rounded-xl p-4 bg-secondary/40 text-sm">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><span className="text-xs text-muted-foreground">Finding</span><div className="font-semibold">Kitchen ventilation deficiency</div></div>
          <div><span className="text-xs text-muted-foreground">Priority</span>
            <div><span className="text-[11px] font-semibold bg-accent-soft text-accent px-2 py-0.5 rounded-full">Medium</span></div>
          </div>
          <div><span className="text-xs text-muted-foreground">Recommended action</span><div className="font-semibold">Install humidity controlled mechanical extraction</div></div>
          <div><span className="text-xs text-muted-foreground">Purpose</span><div className="font-medium">Reduce moisture at source</div></div>
          <div className="sm:col-span-2"><span className="text-xs text-muted-foreground">Repair cost</span><div className="font-semibold text-primary">Confirmed after assessment</div></div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground italic">Repair costs depend on confirmed issue, access, measurements and scope.</p>
    </div>
  );
}

function SectionSignoff() {
  return (
    <div className="space-y-4">
      <blockquote className="border-l-4 border-primary pl-4 italic text-sm text-muted-foreground">
        "The property shows a moderate, manageable damp and mould risk driven primarily by ventilation deficiency and localised external defects. Addressing the kitchen extraction and front elevation rainwater goods should significantly reduce internal moisture load."
      </blockquote>
      <div className="border-t border-border pt-3">
        <p className="text-sm"><span className="font-semibold">Example Surveyor</span>, CRS Energy</p>
        <p className="text-xs text-muted-foreground mt-1">Formal sign off included with Full Diagnostic Survey.</p>
      </div>
    </div>
  );
}

function SectionNextSteps() {
  return (
    <div className="space-y-4">
      <ol className="space-y-3 text-sm">
        {["Review findings",
          "Choose the recommended next step",
          "Book repair works or ask the team for advice"].map((s, i) => (
          <li key={s} className="flex gap-3">
            <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
            <span className="pt-0.5">{s}</span>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={scrollToHeroForm}
        className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-3 text-sm font-bold hover:bg-accent/90 transition-colors shadow-cta w-full sm:w-auto justify-center"
      >
        <Calendar className="h-4 w-4" /> {CTA_LABEL}
      </button>
    </div>
  );
}

/* ──────────────── Tabs ──────────────── */

type TabDef = {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  title: string;
  body: () => ReactElement;
  explanation: ExplanationProps;
};

const TABS: TabDef[] = [
  { id: "overview", label: "Overview", icon: ClipboardList, title: "Report Overview And Risk Summary", body: SectionOverview, explanation: { helps: "See at a glance how serious the issue is and where to focus.", essential: "Severity tier plus headline cause", full: "Plus formal risk summary for lenders", why: "Know the level of concern before spending on repairs." } },
  { id: "scope", label: "Scope", icon: Compass, title: "Instruction And Scope", body: SectionScope, explanation: { helps: "Confirms exactly what was inspected and what was not.", essential: "Single issue or area", full: "Plus multi room, wider property review", why: "Avoid assuming the report covers areas it does not." } },
  { id: "property", label: "Property", icon: Building2, title: "Property Context", body: SectionProperty, explanation: { helps: "Explains how your property's age and build shape damp risk.", essential: "Key context summarised", full: "Plus detailed construction and condition record", why: "Solid wall and cavity homes need different treatments." } },
  { id: "external", label: "External", icon: Home, title: "External Checks", body: SectionExternal, explanation: { helps: "Checks if rainwater, gutters or render are letting water in.", essential: "Key external observations", full: "Plus photo evidence linked to inside symptoms", why: "Stop treating the wrong side of the wall." } },
  { id: "ventilation", label: "Ventilation", icon: Wind, title: "Moisture And Ventilation", body: SectionVentilation, explanation: { helps: "Identifies if condensation and airflow are driving the damp.", essential: "Discussed within the diagnosis", full: "Plus formal ventilation and moisture load record", why: "Practical fixes, not blame for daily living." } },
  { id: "rooms", label: "Room Findings", icon: Layers, title: "Room Findings", body: SectionRoom, explanation: { helps: "Pinpoints what was found in each affected room.", essential: "Concise finding plus next step", full: "Plus room by room evidence trail", why: "Separates the visible symptom from the actual source." } },
  { id: "readings", label: "Readings", icon: FlaskConical, title: "Moisture Readings", body: SectionReadings, explanation: { helps: "Backs up the diagnosis with actual wall moisture readings.", essential: "Spot readings where relevant", full: "Plus per room WME readings table", why: "No guesswork from a single isolated reading." } },
  { id: "thermal", label: "Thermal", icon: Thermometer, title: "Thermal Imaging", body: SectionThermal, explanation: { helps: "Reveals cold spots that quietly drive condensation and mould.", essential: "Not included", full: "Plus cold spot thermal map", why: "Useful when mould follows corners or window reveals." } },
  { id: "photos", label: "Photos", icon: Camera, title: "Photographic Evidence", body: SectionPhotos, explanation: { helps: "Lets you see exactly what each finding is based on.", essential: "Reference photos only", full: "Plus numbered, annotated evidence pack", why: "Strong evidence backs up the recommended action." } },
  { id: "recommendations", label: "Recommendations", icon: ListChecks, title: "Recommendations", body: SectionRecommendations, explanation: { helps: "Turns the findings into a clear repair plan and cost route.", essential: "Concise recommendation plus cost route", full: "Plus expanded scope and priority detail", why: "Avoid paying for the wrong treatment first." } },
  { id: "signoff", label: "Sign Off", icon: PenLine, title: "Surveyor Sign Off", body: SectionSignoff, explanation: { helps: "Confirms who prepared it and if it is fit for formal use.", essential: "Concise written mini report", full: "Plus signed report, valid 3 months", why: "Needed for lenders, insurers, landlords or disputes." } },
  { id: "next", label: "Next Steps", icon: ArrowRight, title: "What Happens Next", body: SectionNextSteps, explanation: { helps: "Shows what happens after the assessment, step by step.", essential: "Fast route to diagnosis plus costs", full: "Plus evidence led route for complex cases", why: "Book the right level of assessment with confidence." } },
];

export function SampleReportViewer({ sectionSelectId = "sample-section-select" }: { sectionSelectId?: string }) {
  const [active, setActive] = useState(0);
  const tab = TABS[active];
  const SectionBody = tab.body;
  const goPrev = () => setActive((i) => Math.max(0, i - 1));
  const goNext = () => setActive((i) => Math.min(TABS.length - 1, i + 1));

  return (
    <>
      <div className="lg:hidden mb-3">
        <label htmlFor={sectionSelectId} className="sr-only">Select report section</label>
        <select
          id={sectionSelectId}
          value={active}
          onChange={(e) => setActive(Number(e.target.value))}
          className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground"
        >
          {TABS.map((t, i) => (
            <option key={t.id} value={i}>{i + 1}. {t.label}</option>
          ))}
        </select>
      </div>

      <div className="hidden lg:flex flex-wrap gap-1.5 mb-4 bg-card rounded-2xl p-2 border border-border">
        {TABS.map((t, i) => {
          const Icon = t.icon;
          const isActive = i === active;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActive(i)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
              aria-current={isActive ? "step" : undefined}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="hidden lg:flex items-center justify-between mb-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Section {active + 1} Of {TABS.length}
        </p>
        <div className="hidden sm:flex items-center gap-1">
          {TABS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === active ? "w-6 bg-primary" : i < active ? "w-3 bg-primary/40" : "w-3 bg-border"}`}
            />
          ))}
        </div>
      </div>

      <div
        className="relative"
        onContextMenu={(e) => e.preventDefault()}
        style={{ userSelect: "none", WebkitUserSelect: "none" } as CSSProperties}
      >
        <div aria-hidden className="pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-3xl">
          <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-8 p-6 opacity-40 md:opacity-60">
            {Array.from({ length: 12 }).map((_, i) => (
              <span
                key={i}
                className="text-primary/10 font-bold text-sm md:text-base whitespace-nowrap"
                style={{ transform: "rotate(-22deg)" }}
              >
                SAMPLE · CRS ENERGY
              </span>
            ))}
          </div>
        </div>

        <div className="relative z-0 bg-card border border-border rounded-3xl shadow-soft">
          <div className="flex items-center gap-2 text-[11px] md:text-xs font-medium text-accent bg-accent-soft border-b border-accent/20 rounded-t-3xl px-3 md:px-4 py-1.5 md:py-2">
            <AlertTriangle className="h-3.5 w-3.5 md:h-4 md:w-4 shrink-0" />
            Fictional sample. Not a report for reliance.
          </div>

          <div className="grid lg:grid-cols-3 gap-5 p-3 md:p-8">
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between gap-2 mb-3 md:mb-4">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                  <span className="inline-flex items-center justify-center h-8 w-8 md:h-10 md:w-10 rounded-full bg-primary text-primary-foreground shrink-0">
                    <tab.icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="hidden lg:block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Section {active + 1} Of {TABS.length}
                    </p>
                    <h3 className="text-sm md:text-xl font-bold text-foreground leading-tight truncate">
                      <span className="lg:hidden">{tab.label}</span>
                      <span className="hidden lg:inline">{tab.title}</span>
                    </h3>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={goPrev}
                    disabled={active === 0}
                    aria-label="Previous section"
                    className="inline-flex items-center justify-center h-8 w-8 md:h-9 md:w-9 rounded-full border border-border bg-card text-foreground disabled:opacity-40 disabled:cursor-not-allowed hover:bg-secondary transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={active === TABS.length - 1}
                    aria-label="Next section"
                    className="inline-flex items-center justify-center h-8 w-8 md:h-9 md:w-9 rounded-full bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-95 transition-opacity"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <SectionBody />
            </div>

            <ExplanationPanel {...tab.explanation} />
          </div>

          <div className="flex items-center justify-between gap-3 px-3 sm:px-5 md:px-8 py-3 md:py-4 border-t border-border">
            <button
              type="button"
              onClick={goPrev}
              disabled={active === 0}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold text-foreground disabled:opacity-40 disabled:cursor-not-allowed hover:bg-secondary transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Previous
            </button>
            <span className="text-xs text-muted-foreground hidden sm:block truncate">
              {tab.label}
            </span>
            <button
              type="button"
              onClick={goNext}
              disabled={active === TABS.length - 1}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-95 transition-opacity"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
